import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { CriteoSponsoredProduct } from '@jbhi-fi/apis/src/criteo/types';

export const SPONSORED_VARIATION = {
  varA: 'varA',
  varB: 'varB',
  varC: 'varC',
  varD: 'varD',
} as const;

export type SponsoredVariationType = (typeof SPONSORED_VARIATION)[keyof typeof SPONSORED_VARIATION];

export const DEFAULT_SPONSORED_VARIATION_TYPE: SponsoredVariationType = SPONSORED_VARIATION.varA;

const SPONSORED_VARIATION_VALUES = new Set<SponsoredVariationType>(Object.values(SPONSORED_VARIATION));

export type SponsoredCriteoItem = {
  source: 'criteo';
  position: number;
  criteoProduct: CriteoSponsoredProduct;
  algoliaProduct?: AlgoliaProductHit;
};

export type CombinedCriteoAndAlgoliaItem =
  | SponsoredCriteoItem
  | {
      source: 'algolia';
      position: number;
      product: AlgoliaProductHit;
    };

export type ExcludedSponsoredProduct = {
  criteoProduct: CriteoSponsoredProduct;
  productKey: string;
  statusCode: number;
};

export type MergeCriteoWithAlgoliaHitsResult = {
  sponsoredItems: Array<SponsoredCriteoItem>;
  organicHits: Array<AlgoliaProductHit>;
  prioritizedHits: Array<AlgoliaProductHit>;
  combinedItems: Array<CombinedCriteoAndAlgoliaItem>;
  missingProductKeys: Array<string>;
  excludedSponsoredProducts: Array<ExcludedSponsoredProduct>;
};

type SponsoredPlacement = SponsoredCriteoItem & {
  algoliaProduct: AlgoliaProductHit;
  sponsoredSlotPosition: number;
};

type SponsoredVariationInput = SponsoredVariationType | null | undefined;

const FALLBACK_SPONSORED_VARIATION_POSITIONS = (sponsoredCount: number): Array<number> =>
  Array.from({ length: sponsoredCount }, (_, index) => index + 1);

const toKey = (value?: string | number | null) => String(value ?? '').trim();

const getAlgoliaKeys = (hit: AlgoliaProductHit): Array<string> => [
  ...new Set([hit.sku, ...(hit.skus ?? [])].map(toKey).filter(Boolean)),
];

const getCriteoKeys = (product: CriteoSponsoredProduct): Array<string> => [
  ...new Set([product.ParentSKU, product.ProductId].map(toKey).filter(Boolean)),
];

const getPrimaryCriteoKey = (product: CriteoSponsoredProduct): string | undefined => getCriteoKeys(product)[0];
const UNIVERSAL_BEACON_DEDUPLICATION_STATUS_CODE = 402;
const UNIVERSAL_BEACON_GENERIC_EXCLUSION_STATUS_CODE = 1;
const UNIVERSAL_BEACON_ROW_SLOT_EXCLUSION_STATUS_CODE = 404;

const toSponsoredHit = (hit: AlgoliaProductHit, criteoProduct: CriteoSponsoredProduct): AlgoliaProductHit => ({
  ...hit,
  isSponsored: true,
  criteoProduct,
});

export const isSponsoredVariationType = (value: unknown): value is SponsoredVariationType =>
  typeof value === 'string' && SPONSORED_VARIATION_VALUES.has(value as SponsoredVariationType);

export const getSponsoredSlotPositions = ({
  sponsoredCount,
  itemsPerRow,
  sponsoredVariationType,
}: {
  sponsoredCount: number;
  itemsPerRow?: number | null;
  sponsoredVariationType?: SponsoredVariationInput;
}): Array<number> => {
  const variationType = isSponsoredVariationType(sponsoredVariationType) ? sponsoredVariationType : null;
  const normalizedItemsPerRow = Number(itemsPerRow);

  if (!variationType || !Number.isInteger(normalizedItemsPerRow) || normalizedItemsPerRow <= 0 || sponsoredCount <= 0) {
    return FALLBACK_SPONSORED_VARIATION_POSITIONS(sponsoredCount);
  }

  const positions: Array<number> = [];
  const addFullRow = (row: number) => {
    for (let column = 1; column <= normalizedItemsPerRow && positions.length < sponsoredCount; column += 1) {
      positions.push((row - 1) * normalizedItemsPerRow + column);
    }
  };
  const addRowStart = (row: number) => {
    if (positions.length < sponsoredCount) {
      positions.push((row - 1) * normalizedItemsPerRow + 1);
    }
  };

  if (variationType === SPONSORED_VARIATION.varA) {
    [1, 3].forEach(addFullRow);
    return positions;
  }

  if (variationType === SPONSORED_VARIATION.varB) {
    [2, 4].forEach(addFullRow);
    return positions;
  }

  for (let row = 1; positions.length < sponsoredCount; row += 1) {
    if (variationType === SPONSORED_VARIATION.varC) {
      if (row === 1) {
        addFullRow(row);
        continue;
      }

      if (row >= 3 && row % 2 === 1) {
        addRowStart(row);
      }
    }

    if (variationType === SPONSORED_VARIATION.varD) {
      addRowStart(row);
    }
  }

  return positions;
};

const getUniqueAlgoliaHits = (algoliaHits: Array<AlgoliaProductHit>): Array<AlgoliaProductHit> => {
  const uniqueHits = new Map<string, AlgoliaProductHit>();

  for (const hit of algoliaHits) {
    const dedupeKey = hit.objectID || getAlgoliaKeys(hit)[0];

    if (dedupeKey && !uniqueHits.has(dedupeKey)) {
      uniqueHits.set(dedupeKey, hit);
    }
  }

  return Array.from(uniqueHits.values());
};

const createAlgoliaLookup = (algoliaHits: Array<AlgoliaProductHit>): Map<string, AlgoliaProductHit> => {
  const lookup = new Map<string, AlgoliaProductHit>();

  for (const hit of algoliaHits.filter(Boolean)) {
    for (const key of getAlgoliaKeys(hit)) {
      lookup.set(key, hit);
    }
  }

  return lookup;
};

export const getMissingCriteoProductKeys = ({
  algoliaHits,
  criteoProducts,
}: {
  algoliaHits: Array<AlgoliaProductHit>;
  criteoProducts: Array<CriteoSponsoredProduct>;
}): Array<string> => {
  const algoliaLookup = createAlgoliaLookup(algoliaHits);
  const missingKeys = new Set<string>();

  criteoProducts.forEach(product => {
    const productKeys = getCriteoKeys(product);
    const hasMatch = productKeys.some(key => algoliaLookup.has(key));

    if (!hasMatch) {
      const primaryKey = getPrimaryCriteoKey(product);

      if (primaryKey) {
        missingKeys.add(primaryKey);
      }
    }
  });

  return Array.from(missingKeys);
};

export const mergeCriteoWithAlgoliaHits = ({
  algoliaHits,
  criteoProducts,
  itemsPerRow,
  sponsoredVariationType,
}: {
  algoliaHits: Array<AlgoliaProductHit>;
  criteoProducts: Array<CriteoSponsoredProduct>;
  itemsPerRow?: number | null;
  sponsoredVariationType?: SponsoredVariationInput;
}): MergeCriteoWithAlgoliaHitsResult => {
  const uniqueAlgoliaHits = getUniqueAlgoliaHits(algoliaHits);
  const algoliaLookup = createAlgoliaLookup(algoliaHits);
  const organicPositions = new Map(uniqueAlgoliaHits.map((hit, index) => [hit.objectID, index + 1]));

  const sponsoredItems = criteoProducts.map((criteoProduct, index) => {
    const algoliaProduct = getCriteoKeys(criteoProduct)
      .map(key => algoliaLookup.get(key))
      .find((hit): hit is AlgoliaProductHit => !!hit);

    return {
      source: 'criteo' as const,
      position: index + 1,
      criteoProduct,
      algoliaProduct,
    };
  });
  const resolvedSponsoredItems = sponsoredItems.filter(
    (item): item is SponsoredCriteoItem & { algoliaProduct: AlgoliaProductHit } => !!item.algoliaProduct
  );
  const organicWinningObjectIds = new Set(
    resolvedSponsoredItems
      .filter(item => (organicPositions.get(item.algoliaProduct.objectID) ?? Number.POSITIVE_INFINITY) < item.position)
      .map(item => item.algoliaProduct.objectID)
  );
  const sponsoredSlotItems = resolvedSponsoredItems.filter(
    item => !organicWinningObjectIds.has(item.algoliaProduct.objectID)
  );
  const sponsoredSlotPositions = getSponsoredSlotPositions({
    sponsoredCount: sponsoredSlotItems.length,
    itemsPerRow,
    sponsoredVariationType,
  });
  const rowCappedSponsoredItems = sponsoredSlotItems.slice(sponsoredSlotPositions.length);

  const sponsoredPlacements: Array<SponsoredPlacement> = sponsoredSlotItems
    .slice(0, sponsoredSlotPositions.length)
    .map((item, index) => ({
      ...item,
      sponsoredSlotPosition: sponsoredSlotPositions[index] ?? index + 1,
    }));
  const organicWinningSponsoredItems = resolvedSponsoredItems.filter(item =>
    organicWinningObjectIds.has(item.algoliaProduct.objectID)
  );
  const matchedSponsoredObjectIds = new Set(sponsoredPlacements.map(item => item.algoliaProduct.objectID));
  const organicHits = uniqueAlgoliaHits.filter(hit => !matchedSponsoredObjectIds.has(hit.objectID));

  const prioritizedHits: Array<AlgoliaProductHit> = [];
  const remainingOrganicHits = [...organicHits];
  const pendingSponsoredPlacements = [...sponsoredPlacements];

  while (pendingSponsoredPlacements.length || remainingOrganicHits.length) {
    const nextPosition = prioritizedHits.length + 1;
    const nextSponsoredPlacement = pendingSponsoredPlacements[0];

    if (nextSponsoredPlacement && nextSponsoredPlacement.sponsoredSlotPosition === nextPosition) {
      prioritizedHits.push(toSponsoredHit(nextSponsoredPlacement.algoliaProduct, nextSponsoredPlacement.criteoProduct));
      pendingSponsoredPlacements.shift();
      continue;
    }

    if (remainingOrganicHits.length) {
      prioritizedHits.push(remainingOrganicHits.shift() as AlgoliaProductHit);
      continue;
    }

    const fallbackSponsoredPlacement = pendingSponsoredPlacements.shift()!;

    prioritizedHits.push(
      toSponsoredHit(fallbackSponsoredPlacement.algoliaProduct, fallbackSponsoredPlacement.criteoProduct)
    );
  }

  const missingProductKeys = sponsoredItems
    .filter(item => !item.algoliaProduct)
    .map(item => getPrimaryCriteoKey(item.criteoProduct))
    .filter((key): key is string => !!key);
  const excludedSponsoredProducts = [
    ...organicWinningSponsoredItems
      .map(item => {
        const productKey = getPrimaryCriteoKey(item.criteoProduct);

        if (!productKey) {
          return null;
        }

        return {
          criteoProduct: item.criteoProduct,
          productKey,
          statusCode: UNIVERSAL_BEACON_DEDUPLICATION_STATUS_CODE,
        };
      })
      .filter((item): item is ExcludedSponsoredProduct => !!item),
    ...sponsoredItems
      .filter(item => !item.algoliaProduct)
      .map(item => {
        const productKey = getPrimaryCriteoKey(item.criteoProduct);

        if (!productKey) {
          return null;
        }

        return {
          criteoProduct: item.criteoProduct,
          productKey,
          statusCode: UNIVERSAL_BEACON_GENERIC_EXCLUSION_STATUS_CODE,
        };
      })
      .filter((item): item is ExcludedSponsoredProduct => !!item),
    ...rowCappedSponsoredItems
      .map(item => {
        const productKey = getPrimaryCriteoKey(item.criteoProduct);

        if (!productKey) {
          return null;
        }

        return {
          criteoProduct: item.criteoProduct,
          productKey,
          statusCode: UNIVERSAL_BEACON_ROW_SLOT_EXCLUSION_STATUS_CODE,
        };
      })
      .filter((item): item is ExcludedSponsoredProduct => !!item),
  ];

  const combinedItems: Array<CombinedCriteoAndAlgoliaItem> = [
    ...sponsoredItems,
    ...organicHits.map((product, index) => ({
      source: 'algolia' as const,
      position: sponsoredItems.length + index + 1,
      product,
    })),
  ];

  return {
    sponsoredItems,
    organicHits,
    prioritizedHits,
    combinedItems,
    missingProductKeys,
    excludedSponsoredProducts,
  };
};
